import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { WeddingReview } from '../types';
import toast from 'react-hot-toast';

export const useVendorReviews = (vendorId: string) => {
  return useQuery({
    queryKey: ['vendorReviews', vendorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wedding_reviews')
        .select(`
          *,
          user:user_id(
             raw_user_meta_data
          )
        `)
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Map user metadata securely
      return data.map((review: any) => ({
        ...review,
        user: {
            full_name: review.user?.raw_user_meta_data?.full_name || 'Anonymous',
            avatar_url: review.user?.raw_user_meta_data?.avatar_url,
            email: review.user?.email
        }
      })) as WeddingReview[];
    },
    enabled: !!vendorId,
  });
};

export const useSubmitReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      vendorId,
      rating,
      content,
    }: {
      vendorId: string;
      rating: number;
      content: string;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('You must be logged in to submit a review');

      const { data, error } = await supabase
        .from('wedding_reviews')
        .insert({
          vendor_id: vendorId,
          user_id: session.user.id,
          rating,
          content,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { vendorId }) => {
      toast.success('Review submitted successfully!');
      // Invalidate reviews query
      queryClient.invalidateQueries({ queryKey: ['vendorReviews', vendorId] });
      // Invalidate vendor details query (to update rating/count)
      queryClient.invalidateQueries({ queryKey: ['weddingVendor', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['weddingVendors'] }); // List view
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to submit review');
    },
  });
};
